// A memory card: one thing Oreia holds about you, and everything you need to argue with it.
//
// Four things have to be true on this card at once, and the layout follows from them:
//   it says what kind of noticing this is (pattern / dream / waking / growth),
//   it names the entries it came from, as chips you can open,
//   the phrases it can prove are tappable in the sentence itself (CitedObservation),
//   and you can affirm it, rewrite it, or make it go away.
//
// The card never renders without sources — that check lives in the API layer
// (`renderableObservation`) so an untraceable claim can't reach any screen, not just this one.

import { Link } from 'react-router-dom';
import { CitedObservation } from '../components/CitedObservation';
import { IconBook, IconCheck, IconSliders } from '../components/Icons';
import type { Observation } from '../api/portrait';

const KIND_LABEL: Record<string, string> = {
  pattern: 'Pattern',
  dream: 'Dream',
  waking: 'Waking',
  growth: 'Growth',
  tension: 'Pattern',
  observation: 'Pattern',
};

/** The four pills the design defines; anything else borrows the neutral one. */
function kindClass(kind: string): string {
  return kind === 'dream' || kind === 'waking' || kind === 'growth' ? kind : 'pattern';
}

/**
 * "noticed 4×" / "surfaced 2 nights ago" — the recency line.
 *
 * A rewritten memory says "in your words" instead, because after a correction the interesting fact
 * about the card is no longer when Oreia noticed it.
 */
export function recencyLabel(observation: Observation, sourceCount: number): string {
  if (observation.userEdited) return 'in your words';
  if (observation.fresh) return 'noticed just now';
  if (sourceCount > 1) return `noticed ${sourceCount}×`;
  return 'noticed once';
}

export interface MemoryCardProps {
  observation: Observation;
  /** Entry title for a source id, when the entry is on this device. */
  entryTitle: (id: string) => string | null;
  onAffirm: () => void;
  onAdjust: () => void;
}

export function MemoryCard({ observation, entryTitle, onAffirm, onAdjust }: MemoryCardProps) {
  const sources = observation.sourceEntryIds;
  const affirmed = observation.affirmed;

  return (
    <div className={`amem${observation.fresh ? ' fresh' : ''}`}>
      <div className="atop">
        <span className={`akind ${kindClass(observation.kind)}`}>
          <span className="d" aria-hidden />
          {KIND_LABEL[observation.kind] ?? 'Pattern'}
        </span>
        <span className="seen">{recencyLabel(observation, sources.length)}</span>
      </div>

      <CitedObservation
        as="div"
        className="aobs"
        text={observation.text}
        citations={observation.citations}
      />

      <div className="asrc">
        {sources.slice(0, 4).map((id, i) => (
          <Link className="schip" key={id} to={`/write/${encodeURIComponent(id)}`}>
            <IconBook />
            {entryTitle(id) || `Entry ${i + 1}`}
          </Link>
        ))}
      </div>

      <div className="afoot">
        {observation.trace ? <span className="trace">{observation.trace}</span> : null}
        <div className="acorrect">
          <button
            type="button"
            className={`actrl affirm${affirmed ? ' on' : ''}`}
            aria-pressed={affirmed}
            onClick={onAffirm}
          >
            <IconCheck />
            {affirmed ? 'Fits' : 'Affirm'}
          </button>
          <button type="button" className="actrl" onClick={onAdjust}>
            <IconSliders />
            Adjust
          </button>
        </div>
      </div>
    </div>
  );
}
